/**
 * The dashboard's i18n (docs/contributing/i18n.md): react-intl (FormatJS, ICU MessageFormat) over JSON catalogs per
 * locale and namespace (src/i18n/locales/<locale>/<namespace>.json). English is bundled; another locale's catalogs
 * load when it is chosen. The provider sets lang, dir and the script font on <html>.
 *
 * Which locale: the user's setting (Me.preferences.locale), else the browser's languages, else the company default,
 * else English (negotiate.ts; <LocaleSync> applies it once the user is known). A switch keeps the page on screen in
 * the previous language until the new catalogs arrive (never unmounting the app). Missing messages: in development and
 * tests they throw (the page fails loudly); in production the English message stands in, never the key.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { IntlProvider, createIntl, useIntl, type IntlShape } from "react-intl";
import { config } from "../config";
import { loadScriptFont } from "./fonts";
import { chain, formatLocale, forSignedOut, localeInfo, match } from "./negotiate.ts";
import type { MessageKey } from "./keys";
import { registerProblemTranslator } from "./problems";
import "./i18n.css";

type Catalog = Record<string, string>;
const LOCALE_KEY = "solaros.locale";

const bundled = import.meta.glob<Record<string, unknown>>("./locales/en/*.json", { eager: true, import: "default" });
const lazy = import.meta.glob<Record<string, unknown>>("./locales/*/*.json", { import: "default" });

function flatten(modules: Record<string, Record<string, unknown>>): Catalog {
  const out: Catalog = {};
  for (const mod of Object.values(modules)) for (const [k, v] of Object.entries(mod)) if (k !== "$meta" && typeof v === "string") out[k] = v;
  return out;
}

const SOURCE_MESSAGES = flatten(bundled);

async function loadCatalog(locale: string): Promise<Catalog> {
  if (locale === "en") return SOURCE_MESSAGES;
  const entries = Object.entries(lazy).filter(([path]) => path.startsWith(`./locales/${locale}/`));
  const modules = await Promise.all(entries.map(async ([path, load]) => [path, await load()] as const));
  return flatten(Object.fromEntries(modules));
}

/** Development, tests and the integration harness (dev sign-in): pseudo-locales allowed, missing keys throw. */
export function devMode(): boolean {
  return import.meta.env.DEV || import.meta.env.MODE === "test" || config.authMode === "dev";
}

function readStored(): string | null {
  try {
    return localStorage.getItem(LOCALE_KEY);
  } catch {
    return null;
  }
}

function store(locale: string | null) {
  try {
    if (locale) localStorage.setItem(LOCALE_KEY, locale);
    else localStorage.removeItem(LOCALE_KEY);
  } catch {
    /* private mode */
  }
}

/** `?locale=` (one of the 21 everywhere, pseudo-locales in development and tests too; remembered for the next signed-out
 * page), else the remembered choice, else the browser's languages. A signed-in user's setting then wins (LocaleSync). */
function initialLocale(): string {
  const allowPseudo = devMode();
  const param = new URLSearchParams(window.location.search).get("locale");
  const chosen = forSignedOut(param, readStored(), navigator.languages ?? [], allowPseudo);
  if (match(param, allowPseudo, "A")) store(chosen);
  return chosen;
}

interface LocaleState {
  locale: string;
  /** The locale forced by `?locale=` in development (it wins over the user's setting there). */
  forced: string | null;
  setLocale: (locale: string) => void;
}

const LocaleContext = createContext<LocaleState | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [forced] = useState(() => {
    const param = devMode() ? new URLSearchParams(window.location.search).get("locale") : null;
    return match(param, true, "A");
  });
  const [locale, setLocaleState] = useState(initialLocale);
  const [messages, setMessages] = useState<Record<string, Catalog>>({ en: SOURCE_MESSAGES });

  const setLocale = useCallback((next: string) => {
    const found = match(next, devMode(), "A");
    if (!found) return;
    setLocaleState(found);
    store(found);
  }, []);

  // Load what the wanted locale needs (its catalogs and script font) ahead of showing it.
  useEffect(() => {
    let live = true;
    for (const code of chain(locale)) {
      if (messages[code] || code === "en") continue;
      loadCatalog(code).then((catalog) => live && setMessages((m) => ({ ...m, [code]: catalog })));
    }
    loadScriptFont(localeInfo(locale).script);
    return () => {
      live = false;
    };
  }, [locale, messages]);

  // The locale on screen: the wanted one once its catalogs are in, until then the one before it. Switching language
  // re-renders the page in place; it never unmounts the app (sign-in, open dialogs and caches stay as they are).
  const ready = chain(locale).every((code) => messages[code]);
  const [shown, setShown] = useState<string | null>(() => (ready ? locale : null));
  const current = ready ? locale : shown;
  useEffect(() => {
    if (ready) setShown(locale);
  }, [ready, locale]);
  useEffect(() => {
    if (!current) return;
    const info = localeInfo(current);
    document.documentElement.lang = current;
    document.documentElement.dir = info.dir;
  }, [current]);

  const merged = useMemo(() => {
    if (!current) return SOURCE_MESSAGES;
    // Production: the chain's messages over English. Development and tests: the locale's own only, so a missing key
    // throws instead of quietly showing English.
    const codes = chain(current).filter((c) => messages[c]).reverse();
    const layers = devMode() && current !== "en" ? codes.filter((c) => c !== "en") : codes;
    return Object.assign({}, ...layers.map((c) => messages[c]));
  }, [current, messages]);

  // API problems and other text outside React (errorMessage) in this language: i18n/problems.ts
  useEffect(() => {
    if (!current) return;
    const intl = createIntl({ locale: formatLocale(current), defaultLocale: "en", messages: merged, onError: () => undefined });
    registerProblemTranslator({
      locale: current,
      has: (key) => key in merged,
      format: (key, values) => intl.formatMessage({ id: key }, values as never) as string,
    });
  }, [current, merged]);

  const state = useMemo(() => ({ locale, forced, setLocale }), [locale, forced, setLocale]);
  if (!current) return null; // only on the very first load of a non-English locale (a few kB), before the app exists
  return (
    <LocaleContext.Provider value={state}>
      <IntlProvider
        locale={formatLocale(current)}
        defaultLocale="en"
        messages={merged}
        onError={(err) => {
          if (devMode()) throw err;
        }}
      >
        {children}
      </IntlProvider>
    </LocaleContext.Provider>
  );
}

export function useLocale(): LocaleState {
  const state = useContext(LocaleContext);
  if (!state) throw new Error("useLocale outside <I18nProvider>");
  return state;
}

export type Translate = (key: MessageKey, values?: Record<string, string | number | Date | ((chunks: ReactNode[]) => ReactNode)>) => string;

/** `const t = useT(); t("shell.nav.designs")` — typed keys (keys.ts), ICU values. */
export function useT(): Translate {
  const intl = useIntl();
  return useCallback<Translate>((key, values) => intl.formatMessage({ id: key }, values as never) as string, [intl]);
}

/** Rich text: `tRich(intl, "shell.adminBanner", { b: (c) => <b>{c}</b>, org })`. */
export function useIntlShape(): IntlShape {
  return useIntl();
}
